import { SEO } from "@/components/seo/SEO";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, Search, CheckCircle, XCircle, Clock, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";

const VulnerabilityDisclosurePolicyEN = () => {
  const navigate = useNavigate();
  const lastUpdated = "December 20, 2024";
  const companyName = "EasyFinder";
  const securityEmail = "security@easyfinder.fr";
  const websiteUrl = "https://easyfinder.fr";

  return (
    <>
      <SEO
        title="Vulnerability Disclosure Policy | EasyFinder"
        description="EasyFinder Responsible Vulnerability Disclosure Policy. How to report a security vulnerability responsibly."
        canonical="/vulnerability-disclosure-policy-en"
      />
      <div className="min-h-screen bg-background">
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <LanguageSwitcher frPath="/vulnerability-disclosure-policy" enPath="/vulnerability-disclosure-policy-en" />
          </div>

          <article className="prose prose-slate dark:prose-invert max-w-none">
            <div className="flex items-center gap-3 mb-4">
              <Search className="h-8 w-8 text-primary" />
              <h1 className="text-3xl font-bold mb-0">Vulnerability Disclosure Policy</h1>
            </div>
            <p className="text-muted-foreground mb-8">
              Last updated: {lastUpdated}
            </p>

            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
              <p>
                {companyName} is committed to protecting the security of our users' data. 
                We value the assistance of the security research community in identifying 
                and addressing vulnerabilities in our systems.
              </p>
              <p>
                This policy provides security researchers with clear guidelines for conducting 
                vulnerability discovery activities and explains how to report discovered 
                vulnerabilities responsibly.
              </p>
            </section>

            {/* Guidelines */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Shield className="h-6 w-6" />
                2. Guidelines for Researchers
              </h2>
              <p>We ask that you:</p>
              <ul>
                <li>
                  <strong>Notify us promptly</strong> after discovering a real or potential vulnerability
                </li>
                <li>
                  <strong>Allow us reasonable time</strong> (minimum 90 days) to resolve the issue 
                  before any public disclosure
                </li>
                <li>
                  <strong>Avoid privacy violations</strong>, degradation of user experience, 
                  disruption to production systems, and destruction or manipulation of data
                </li>
                <li>
                  <strong>Limit exploits</strong> to the minimum necessary to confirm a vulnerability exists
                </li>
                <li>
                  <strong>Do not use exploits</strong> to compromise or obtain data, 
                  establish persistent access, or pivot to other systems
                </li>
                <li>
                  <strong>Stop testing immediately</strong> if you encounter sensitive data 
                  (personal data, financial information, trade secrets) and notify us
                </li>
                <li>
                  <strong>Do not submit</strong> a large volume of low-quality reports
                </li>
              </ul>
            </section>

            {/* Safe Harbor */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">3. Safe Harbor</h2>
              <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <p className="text-green-800 dark:text-green-200">
                  Security research conducted in accordance with this policy is considered:
                </p>
                <ul className="text-green-700 dark:text-green-300 mt-2">
                  <li>✅ <strong>Authorized</strong> under applicable anti-hacking laws</li>
                  <li>✅ <strong>Authorized</strong> under data protection laws</li>
                  <li>✅ <strong>Exempt</strong> from restrictions in our Terms of Service that 
                      might interfere with security research</li>
                </ul>
                <p className="text-green-800 dark:text-green-200 mt-4 font-semibold">
                  We will work with you to understand and quickly resolve the issue, 
                  and {companyName} will not suggest or pursue legal action in connection 
                  with your research conducted in accordance with this policy.
                </p>
              </div>
            </section>

            {/* Scope */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">4. Scope</h2>
              
              <h3 className="text-xl font-medium mt-6 mb-3 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                In-Scope Systems
              </h3>
              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">System</th>
                    <th className="border border-border p-3 text-left">URL / Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3">Main Web Application</td>
                    <td className="border border-border p-3">
                      <a href={websiteUrl} className="text-primary underline">{websiteUrl}</a>
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Backend API</td>
                    <td className="border border-border p-3">
                      Supabase Edge Functions endpoints accessible via {websiteUrl}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Authentication</td>
                    <td className="border border-border p-3">
                      Google OAuth flows and email/password authentication
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Document Sharing</td>
                    <td className="border border-border p-3">
                      Share links: {websiteUrl}/share/* and {websiteUrl}/collection/*
                    </td>
                  </tr>
                </tbody>
              </table>

              <h3 className="text-xl font-medium mt-6 mb-3 flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                Out of Scope
              </h3>
              <p>The following systems and services are <strong>explicitly excluded</strong> from scope:</p>
              <ul>
                <li>Infrastructure of our sub-processors (Supabase, Cloudflare, Stripe, Google)</li>
                <li>Third-party integrated services (Gmail API, Dropbox API)</li>
                <li>Third-party applications not developed by {companyName}</li>
                <li>Any domain not listed above</li>
              </ul>
              <p className="mt-4">
                Vulnerabilities discovered in third-party solutions should be reported 
                directly to the relevant vendor according to their own disclosure policy.
              </p>
              <p>
                <strong>If in doubt</strong> about whether a system is in scope, 
                contact us at <a href={`mailto:${securityEmail}`} className="text-primary underline">{securityEmail}</a> before testing.
              </p>
            </section>

            {/* Prohibited Testing */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">5. Prohibited Testing Types</h2>
              <p>The following types of testing are <strong>not authorized</strong>:</p>
              
              <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-4 mt-4">
                <ul className="text-red-700 dark:text-red-300 space-y-2">
                  <li>❌ <strong>Denial of service (DoS/DDoS)</strong> network or application testing</li>
                  <li>❌ <strong>Physical testing</strong> (facility access, tailgating)</li>
                  <li>❌ <strong>Social engineering</strong> (phishing, vishing, pretexting) against our employees or users</li>
                  <li>❌ <strong>Spam or mass messaging</strong></li>
                  <li>❌ <strong>Aggressive automated testing</strong> without appropriate rate limiting</li>
                  <li>❌ <strong>Modifying or deleting</strong> other users' data</li>
                  <li>❌ <strong>Accessing accounts</strong> of other users without their explicit consent</li>
                </ul>
              </div>
            </section>

            {/* Qualifying Vulnerabilities */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">6. Qualifying Vulnerabilities</h2>
              
              <h3 className="text-xl font-medium mt-6 mb-3">Examples of vulnerabilities we're looking for</h3>
              <ul>
                <li>SQL, NoSQL, or command injection</li>
                <li>Cross-Site Scripting (XSS) - Stored, Reflected, DOM-based</li>
                <li>Cross-Site Request Forgery (CSRF)</li>
                <li>Authentication or authorization bypass</li>
                <li>Insecure Direct Object Reference (IDOR)</li>
                <li>Sensitive data exposure</li>
                <li>Server-Side Request Forgery (SSRF)</li>
                <li>Critical security misconfiguration</li>
                <li>Business logic flaws allowing unauthorized actions</li>
                <li>Row Level Security (RLS) policy bypass</li>
              </ul>

              <h3 className="text-xl font-medium mt-6 mb-3">Generally out-of-scope vulnerabilities</h3>
              <ul>
                <li>Vulnerabilities requiring physical access to user's device</li>
                <li>Automated scanner reports without proof of exploitability</li>
                <li>Clickjacking on pages without sensitive actions</li>
                <li>Self-XSS (XSS requiring victim interaction)</li>
                <li>Missing security headers without demonstrated impact</li>
                <li>User enumeration via login error messages</li>
                <li>Weak password policy (already documented)</li>
                <li>Missing rate limiting without demonstrated impact</li>
                <li>Software version disclosure without associated vulnerability</li>
              </ul>
            </section>

            {/* Reporting */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Mail className="h-6 w-6" />
                7. How to Report a Vulnerability
              </h2>
              
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 mb-6">
                <p className="text-lg font-semibold mb-2">📧 Reporting Email:</p>
                <a href={`mailto:${securityEmail}`} className="text-2xl text-primary underline font-mono">
                  {securityEmail}
                </a>
                <p className="text-sm text-muted-foreground mt-2">
                  Reports can be submitted anonymously.
                </p>
              </div>

              <h3 className="text-xl font-medium mt-6 mb-3">Information to include in your report</h3>
              <p>To help us process your report effectively, please include:</p>
              
              <div className="bg-muted rounded-lg p-4 font-mono text-sm mt-4">
                <p><strong>📋 VULNERABILITY REPORT</strong></p>
                <br />
                <p><strong>1. Vulnerability description:</strong></p>
                <p>[Clearly describe the security issue]</p>
                <br />
                <p><strong>2. Location:</strong></p>
                <p>[URL, endpoint, affected feature]</p>
                <br />
                <p><strong>3. Potential impact:</strong></p>
                <p>[What data or functions are at risk?]</p>
                <br />
                <p><strong>4. Steps to reproduce:</strong></p>
                <p>1. [First step]</p>
                <p>2. [Second step]</p>
                <p>3. [...]</p>
                <br />
                <p><strong>5. Evidence:</strong></p>
                <p>[Screenshots, HTTP requests, logs - WITHOUT other users' data]</p>
                <br />
                <p><strong>6. Suggested fix (optional):</strong></p>
                <p>[Your recommendations to fix the issue]</p>
                <br />
                <p><strong>7. Your contact info (optional):</strong></p>
                <p>[If you wish to be kept informed of the resolution]</p>
              </div>

              <p className="mt-4">
                <strong>Language:</strong> Reports in English or French are accepted.
              </p>
            </section>

            {/* Our Commitment */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Clock className="h-6 w-6" />
                8. Our Commitments
              </h2>
              
              <p>When you choose to share your contact information, we commit to:</p>
              
              <table className="w-full border-collapse border border-border mt-4">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-3 text-left">Commitment</th>
                    <th className="border border-border p-3 text-left">Timeline</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-3">Acknowledge receipt of your report</td>
                    <td className="border border-border p-3 font-semibold">≤ 3 business days</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Confirm vulnerability validity</td>
                    <td className="border border-border p-3 font-semibold">≤ 10 business days</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Provide fix progress updates</td>
                    <td className="border border-border p-3 font-semibold">Regular</td>
                  </tr>
                  <tr>
                    <td className="border border-border p-3">Notify you of resolution</td>
                    <td className="border border-border p-3 font-semibold">Upon deployment</td>
                  </tr>
                </tbody>
              </table>

              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mt-6">
                <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
                  💬 Transparent Communication
                </h4>
                <p className="text-blue-700 dark:text-blue-300">
                  We are committed to transparent and respectful communication. 
                  We are open to discussion and ready to answer your questions 
                  about how your report is being handled.
                </p>
              </div>
            </section>

            {/* Recognition */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">9. Recognition</h2>
              <p>
                While we do not currently have a paid bug bounty program, 
                we sincerely appreciate contributions from the security community.
              </p>
              <p>
                With your consent, we can:
              </p>
              <ul>
                <li>Mention you in our Hall of Fame (acknowledgments page)</li>
                <li>Provide a recommendation letter</li>
                <li>Keep you informed of resolutions and improvements</li>
              </ul>
            </section>

            {/* Legal */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">10. Legal Framework</h2>
              <p>
                This policy is designed in accordance with:
              </p>
              <ul>
                <li>ISO 29147 (Vulnerability disclosure)</li>
                <li>ISO 30111 (Vulnerability handling processes)</li>
                <li>EU Directive on security of network and information systems (NIS2)</li>
              </ul>
              <p className="mt-4">
                By participating in this program, you agree to comply with 
                all applicable laws and this policy.
              </p>
            </section>

          </article>

          <div className="mt-8 pt-8 border-t text-center text-sm text-muted-foreground">
            <p>© {new Date().getFullYear()} {companyName}. All rights reserved.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default VulnerabilityDisclosurePolicyEN;
